import { GoalCard } from "@/modules/planning/components/goals/goal-card";
import { ProposalCard } from "@/modules/planning/components/proposals/proposal-card";
import { GoalUIState } from "@/modules/planning/providers/goal-context-provider";
import { ProposalUIState } from "@/modules/planning/providers/proposal-provider";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { Page } from "@core/components/layout/page";
import { CreateGoal } from "@planning/components/goals/create-goal";
import { GoalsEmpty } from "@planning/components/goals/empty";
import { ProposalsEmpty } from "@planning/components/proposals/empty";
import { createFileRoute } from "@tanstack/react-router";
import { GOAL_OPTIONS } from "creso-core/constants";
import { useClient, useGoals, useProposals } from "creso-core/hooks";
import { Client } from "creso-core/types";
import { Button, V2Loader } from "web-design-system";

export const Route = createFileRoute("/(auth)/_headed/clients/$id/planning")({
  component: RouteComponent,
  validateSearch: (search) => {
    const params: {
      goal_id?: number;
      proposal_id?: number;
      state?: GoalUIState | ProposalUIState;
      type?: string;
      goal_type?: (typeof GOAL_OPTIONS)[number]["value"];
    } = {};
    if (search.type) {
      params.type = search.type as string;
    }
    if (search.state) {
      params.state = search.state as GoalUIState | ProposalUIState;
    }
    if (search.goal_id) {
      params.goal_id = search.goal_id as number;
    }
    if (search.proposal_id) {
      params.proposal_id = search.proposal_id as number;
    }
    if (search.goal_type) {
      params.goal_type =
        search.goal_type as (typeof GOAL_OPTIONS)[number]["value"];
    }
    return params;
  },
});

// const GOAL_EMPTY_STATE_TEXT = {
//   HOUSE: "Plan to buy a house",
//   MARRIAGE: "Plan for the wedding",
//   CAR: "Plan to buy a car",
//   RETIREMENT: "Plan for retirement",
//   CHILD_EDU: "Plan for child’s education",
//   CUSTOM: "Add custom target",
// };

function RouteComponent() {
  const { id: clientId } = Route.useParams();

  const navigate = Route.useNavigate();

  const { data: client, isLoading: isLoadingClient } = useClient(
    Number(clientId)
  );

  const { data: goals, isLoading: isLoadingGoals } = useGoals({
    clientId: Number(clientId),
  });

  const { data: proposals, isLoading: isLoadingProposals } = useProposals({
    clientId: Number(clientId),
  });

  return (
    <>
      <Page.Header
        title="Goal Planning"
        className="relative"
        actions={
          <>
            {goals?.results.length ? (
              <CreateGoal client={client as Client} />
            ) : null}
            {proposals?.length ? (
              <Button
                onClick={() =>
                  navigate({
                    to: Route.fullPath,
                    search: {
                      client_id: Number(clientId),
                      modal: "proposal",
                      state: "init",
                    },
                    replace: true,
                  })
                }
              >
                Create proposal
              </Button>
            ) : null}
          </>
        }
      />
      <Page.Body>
        {/* Goals Section */}
        <V2Loader
          isLoading={isLoadingGoals || isLoadingProposals || isLoadingClient}
          className="flex flex-1 flex-col gap-6 overflow-hidden"
        >
          <ConditionalRenderer condition={!!goals?.results.length}>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 size-full md:grid-cols-2 lg:grid-cols-3 gap-4">
                {goals?.results.map((g) => (
                  <GoalCard goal={g} key={g.id} />
                ))}
              </div>
            </div>
          </ConditionalRenderer>

          <ConditionalRenderer condition={!goals?.results.length}>
            <GoalsEmpty client={client as Client} />
          </ConditionalRenderer>

          <ConditionalRenderer condition={!proposals?.length}>
            <ProposalsEmpty client={client as Client} />
          </ConditionalRenderer>

          {/* Proposals Section */}
          <ConditionalRenderer condition={!!proposals?.length}>
            <div className="flex flex-col gap-4">
              <h5 className="text-sm font-semibold tracking-wide">Proposals</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {proposals?.map((p) => (
                  <ProposalCard
                    proposal={p}
                    key={p.id}
                    client={client as Client}
                  />
                ))}
              </div>
            </div>
          </ConditionalRenderer>
        </V2Loader>
      </Page.Body>
    </>
  );
}
